import axios from "axios";
import { ReqXSettings } from "__SYSDefault/Debug";
import { DOMAIN } from "__SYSDefault/Domain";
import GFunc from "./__GFunc";
import GState from "./__GState";
import _ from "lodash";
import FormAttachmentX from "./FormAttachmentX";
import ReqConfig from "__SYSDefault/_ReqConfig";
import IdleX from "./IdleX";
import ErrorHandle from "__SYSDefault/ErrorHandle";
import ZMath from "./ZMath";
import Task from "./Task";
/**
 * @typedef {import("axios").AxiosRequestConfig} AxiosRequestConfig
 */

class ReqX {

  static Alert = {
    Success: () => {
      GFunc.Alert({
        EN: "Success",
        TC: "成功"
      }, "success");
    },
    Fail: (payload) => {
      let {message, errorCode} = payload || {};
      if(message){
        GFunc.Alert(payload.message, 
          ZMath.IsBetween(errorCode, 7000, 8000, "[)") ? "error" : "system");
      }else{
        GFunc.Alert({
          EN: "The server is temporarily unavailable, please try again later.",
          TC: "系統暫時無法處理你的要求，請稍後再試。"
        }, "error");
      }
    },
    CannotConnect: () => {
      console.error("--- Cannot connect to server ---");
    }
  }

  static async #SendCore(path, url, payloadOut, config, loading, noJWT, retry = 5){
    if(ReqXSettings.OutLog.show) {
      if(ReqXSettings.OutLog.object){
        console.log("%c[OUT] " + path, "color: " + ReqXSettings.OutLog.color, payloadOut);
      }else{
        console.log("%c[OUT] " + path, "color: " + ReqXSettings.OutLog.color);
      }
    }
    if(loading) GFunc.Loading(true);
    let res = await axios.post(url, payloadOut, config);

    if(res?.data?.payload?.errorCode === 4001){
      console.warn("[x] error 4001 :: No refresh token.");
      GFunc.Logout?.();
      return {...res, abort: true};
    }

    //expired
    if(res?.data?.payload?.errorCode === 4002){
      console.warn("[x] error 4002 :: Expired token.");
      if(!noJWT){
        await IdleX.Sleep(5000);
        // const PublicClientApplication = new PublicClientApplication(msalConfig);
        let accessToken = await Task.Retry(ReqConfig.GetAccessToken);
        config = {
          ...config,
          headers: { 
            ...config.headers,
            Authorization: `Bearer ${accessToken}` 
          },
        };
        if(retry > 0){
          console.warn("[x] error 4002 :: Retry.");
          return await this.#SendCore(path, url, payloadOut, config, loading, noJWT, --retry);
        }else{
          console.error("[x] error 4002 :: Expired token. Retry limit reached.");
          GFunc.Logout?.();
          return {...res, abort: true};
        }
      }
    }

    if(ReqXSettings.InLog.show) {
      if(ReqXSettings.InLog.object){
        console.log("%c[IN] " + path, "color: " + ReqXSettings.InLog.color, res.data);
      }else{
        console.log("%c[IN] " + path, "color: " + ReqXSettings.InLog.color);
      }
    }

    return res;
  }

  /**
   * @typedef {Object} SendQOptions
   * @property {Boolean} streamed - If the request is streamed.
   * @property {Boolean} withAttachment - If the request has attachments.
   * @property {Boolean} getBlob - If the request gets blob.
   * @property {Function} success - Success callback.
   * @property {Function} fail - Fail callback.
   * @property {Function} error - Error callback.
   * @property {Boolean} loading - If the request is loading.
   * @property {Boolean} noJWT - If the request does not require JWT.
   * @property {AxiosRequestConfig} config - Axios request config.
   * @property {Function} startFunc - Function to call when the request starts.
   * @property {Function} endFunc - Function to call when the request ends.
   * @property {(startRes, chunk, fullMessage) => void} aliveStreamer - Function to call when the request is streaming.
   * @property {Boolean} lineStream - If the streaming is line by line.
   * @property {import("react").Ref} readerRef - Ref to store the reader.
   * @property {AbortSignal} signal - Abort signal to cancel the request.
   * 
   * @param {String} path
   * @param {*} data
   * @param {SendQOptions} options
   * 
   * @returns {Promise<{
   *  Success: Boolean,
   *  blob: *,
   *  payload: *
   * }>}
   * 
   */
  static async SendQ(path, data, {
    streamed = false,
    withAttachment = false,
    getBlob = false,
    success = null, 
    fail = this.Alert.Fail, 
    error = this.Alert.CannotConnect, 
    loading = false, 
    noJWT = false, 
    config = {},
    startFunc = null,
    endFunc = null,
    aliveStreamer = null,
    lineStream = false,
    readerRef = null,
    signal = undefined
  } = {}){
    if(streamed && withAttachment){
      return await this.SendBEStreamWithAttachments(path, data, 
        startFunc, aliveStreamer, endFunc, error, noJWT, config, lineStream, readerRef, signal);
    }else if(streamed){
      return await this.SendBEStream(path, data, 
        startFunc, aliveStreamer, endFunc, error, noJWT, config, lineStream, readerRef, signal);
    }else if(withAttachment){
      return await this.SendBEWithAttachments(path, data, 
        success, fail, error, loading, noJWT, config);
    }else if(getBlob){
      return await this.SendBEAndGetBlob(path, data, 
        success, fail, error, loading, noJWT, config); 
    }else{
      return await this.SendBE(path, data, 
        success, fail, error, loading, noJWT, config);
    }
  }

  /**
   * Standard Request Sending Box
   * @param {String} path 
   * @param {*} data 
   * @param {Function} success 
   * @param {Function} fail 
   * @param {Function} error 
   * @param {Boolean} loading
   * @param {Boolean} noJWT 
   * @param {AxiosRequestConfig} config
   * @returns {Promise<{
   *  Success: Boolean,
   *  payload: *
   * }>}
   */
  static async SendBE(path, data = {},  
    success = null, 
    fail = this.Alert.Fail, 
    error = this.Alert.CannotConnect, loading = false, noJWT = false, config = {}){

    let url = DOMAIN + path;
    let payloadOut = {
      data: data,
      addOns: await ReqConfig.GetAddOns()
    };
    
    try {
      if(!noJWT){
        // const PublicClientApplication = new PublicClientApplication(msalConfig);
        let accessToken = await Task.Retry(ReqConfig.GetAccessToken);
        config = {
          ...config,
          headers: { 
            ...config.headers,
            Authorization: `Bearer ${accessToken}` 
          },
        };
      }
    }catch(e){
      
      let x = GState.recoverPath;

      if(_.isEmpty(x)){
        let {pathname, search} = window?.location;
        GState.setRecoverPath(`${pathname}${search}`);
      }

      console.error("token error", e);
      ErrorHandle.ReqX(e);

      return {Success: false, payload: {errorCode: 4001, message: "No refresh token."}};
    }
    
    try {
      let res = await this.#SendCore(path, url, payloadOut, config, loading, noJWT);
      if(!res || res?.abort) return {Success: false};

      let {Success, payload} = res.data;
      if (Success === true) {
        success?.(payload);
        return {
          Success: true,
          payload: payload
        };
      } else {
        fail?.(payload);
        return {
          Success: false, 
          payload: payload
        };
      }
    }catch(e){
      error?.(e);
      return {Success: false};
    }finally{
      if(loading) setTimeout(() => GFunc.Loading(false), 100);
    }
  }

  /**
   * 
   * @param {String} path 
   * @param {*} data 
   * @param {Function} startFunc 
   * @param {(startRes, chunk, fullMessage) => Promise<void>} aliveStreamer 
   * @param {Function} endFunc 
   * @param {Function} error 
   * @param {Boolean} noJWT 
   * @param {AxiosRequestConfig} config 
   * @param {Boolean} lineStream
   * @param {import("react").Ref} readerRef - Ref to store the reader
   * @param {AbortSignal} signal - Abort signal to cancel the request
   * @returns 
   */
  static async SendBEStream(path, data = {}, 
    startFunc = null, aliveStreamer = null, endFunc = null,
    error = this.Alert.CannotConnect,
    noJWT = false, config = {}, lineStream = false, readerRef = null, signal = undefined ){
    let url = DOMAIN + path;
    let payloadOut = {
      data: data,
      addOns: await ReqConfig.GetAddOns()
    };

    try {
      if(!noJWT){
        // const PublicClientApplication = new PublicClientApplication(msalConfig);
        let accessToken = await Task.Retry(ReqConfig.GetAccessToken);
        config = {
          ...config,
          headers: { 
            ...config.headers,
            Authorization: `Bearer ${accessToken}` 
          },
        };
      }
    }catch(e){  
      let x = GState.recoverPath;
      if(_.isEmpty(x)){
        let {pathname, search} = window?.location;
        GState.setRecoverPath(`${pathname}${search}`);
      }
      ErrorHandle.ReqX(e);

      return {Success: false, payload: {errorCode: 4001, message: "No refresh token."}};
    }

    try{
      let res = await fetch(url, 
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Connection": "keep-alive",
            "Response-Type": "stream",
            ...config.headers
          },
          body: JSON.stringify(payloadOut),
          signal
        });

      let reader = res?.body?.getReader();
      if(readerRef) readerRef.current = reader;
      let decoder = new TextDecoder("utf-8");

      let startRes = await startFunc?.();

      let fullMessage = "";
      let buffer = "";
      while(true){
        const {value, done} = await reader?.read?.() || {};
        if(done) break;
        let chunk = decoder.decode(value, {stream: true});
        fullMessage += chunk;
        if(lineStream){
          buffer += chunk;
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";
          await aliveStreamer?.(startRes, chunk, lines);
        }else{
          await aliveStreamer?.(startRes, chunk, fullMessage);
        }
      }

      await endFunc?.(startRes);

      return {
        Success: true
      };

    }catch(e){
      error?.(e);
      return {Success: false};
    }
        
  }

  /**
   * Standard Request Sending Box
   * @param {String} path 
   * @param {*} data 
   * @param {Function} success 
   * @param {Function} fail 
   * @param {Function} error 
   * @param {Boolean} loading
   * @param {Boolean} noJWT 
   * @param {AxiosRequestConfig} config
   * @returns {Promise<{
   *  Success: Boolean,
   *  blob: *,
   *  payload: *
   * }>}
   */
  static async SendBEAndGetBlob(path, data = {},  
    success = this.Alert.Success, 
    fail = this.Alert.Fail, 
    error = this.Alert.CannotConnect, loading = false, noJWT = false, config = {}){

    let url = DOMAIN + path;
    let payloadOut = {
      data: data,
      addOns: await ReqConfig.GetAddOns()
    };
    if(!noJWT){
      // const PublicClientApplication = new PublicClientApplication(msalConfig);
      let accessToken = await Task.Retry(ReqConfig.GetAccessToken);
      config = {
        ...config,
        headers: { 
          ...config.headers, 
          Authorization: `Bearer ${accessToken}` 
        },
        responseType: "blob", //!important
      };
    }

    try {
      let res = await this.#SendCore(path, url, payloadOut, config, loading, noJWT);
      if(!res || res?.abort) return {Success: false};
      try{
        let json = JSON.parse(await res?.data?.text?.());
        if(!json?.Success){
          return {Success: false, payload: json?.payload};
        }
      }catch{}
      
      return {Success: true, blob: res?.data, payload: ""};

    }catch(e){   
      if(error) error(e);
      return {Success: false};
    }finally{
      if(loading) setTimeout(() => GFunc.Loading(false), 100);
    }
  }

  /**
   * Standard Request Sending Box
   * @param {String} path 
   * @param {*} data 
   * @param {Function} success 
   * @param {Function} fail 
   * @param {Function} error 
   * @param {Boolean} loading
   * @param {Boolean} noJWT 
   * @param {AxiosRequestConfig} config
   * @returns {Promise<{
   *  Success: Boolean,
   *  blob: *,
   *  payload: *
   * }>}
   */
  static async SendBEWithAttachments(path, data = {}, 
    success = this.Alert.Success, 
    fail = this.Alert.Fail, 
    error = this.Alert.CannotConnect, loading = false, noJWT = false, config = {}){

    let {allAssets, linkedFormData} = FormAttachmentX.SeparateAndLink(data);

    if(allAssets.length <= 0){
      return await this.SendBE(path, linkedFormData, success, fail, error, loading, noJWT, config);
    }

    let payloadOut = new FormData();
    payloadOut.append("data", JSON.stringify(linkedFormData ?? {}));
    payloadOut.append("addOns", JSON.stringify(await ReqConfig.GetAddOns()));
    allAssets.forEach(img => {
      payloadOut.append("attachments", img);
    });
    console.log(payloadOut, allAssets);

    let url = DOMAIN + path;
    if(!noJWT){
      // const PublicClientApplication = new PublicClientApplication(msalConfig);
      let accessToken = await Task.Retry(ReqConfig.GetAccessToken);
      config = {
        ...config,
        headers: { 
          ...config.headers, 
          Authorization: `Bearer ${accessToken}` 
        },
        "Content-Type": "multipart/form-data"
      };
    }

    try {
      let res = await this.#SendCore(path, url, payloadOut, config, loading, noJWT);
      if(!res || res?.abort) return {Success: false};

      let {Success, payload} = res.data;
      if (Success === true) {
        success?.(payload);
        return {
          Success: true,
          payload: payload
        };
      } else {
        fail?.(payload);
        return {
          Success: false, 
          payload: payload
        };
      }
    }catch(e){
      if(error) error(e);
      return {Success: false};
    }finally{
      if(loading) setTimeout(() => GFunc.Loading(false), 100);
    }

  }

  /**
   * 
   * @param {String} path 
   * @param {*} data 
   * @param {Function} startFunc 
   * @param {(startRes, chunk, fullMessage) => Promise<void>} aliveStreamer 
   * @param {Function} endFunc 
   * @param {Function} error 
   * @param {Boolean} noJWT 
   * @param {AxiosRequestConfig} config 
   * @param {Boolean} lineStream
   * @param {import("react").Ref} readerRef - Ref to store the reader
   * @returns 
   */
  static async SendBEStreamWithAttachments(path, data = {}, 
    startFunc = null, aliveStreamer = null, endFunc = null,
    error = this.Alert.CannotConnect,
    noJWT = false, config = {}, lineStream = false, readerRef = null, signal = undefined ){

    let {allAssets, linkedFormData} = FormAttachmentX.SeparateAndLink(data);

    if(allAssets.length <= 0){
      return await this.SendBEStream(path, linkedFormData, 
        startFunc, aliveStreamer, endFunc, error, noJWT, config, lineStream);
    }

    let payloadOut = new FormData();
    payloadOut.append("data", JSON.stringify(linkedFormData ?? {}));
    payloadOut.append("addOns", JSON.stringify(await ReqConfig.GetAddOns()));
    allAssets.forEach(img => {
      payloadOut.append("attachments", img);
    });

    let url = DOMAIN + path;

    try {
      if(!noJWT){
        // const PublicClientApplication = new PublicClientApplication(msalConfig);
        let accessToken = await Task.Retry(ReqConfig.GetAccessToken);
        config = {
          ...config,
          headers: { 
            ...config.headers,
            Authorization: `Bearer ${accessToken}` 
          }
        };
      }
    }catch(e){  
      let x = GState.recoverPath;
      if(_.isEmpty(x)){
        let {pathname, search} = window?.location;
        GState.setRecoverPath(`${pathname}${search}`);
      }
      ErrorHandle.ReqX(e);
      return {Success: false, payload: {errorCode: 4001, message: "No refresh token."}};
    }

    try{
      let res = await fetch(url, 
        {
          method: "POST",
          "Content-Type": "multipart/form-data",
          headers: {
            "Connection": "keep-alive",
            "Response-Type": "stream",
            ...config.headers
          },
          body: payloadOut,
          signal
        });

      let reader = res?.body?.getReader();
      if(readerRef) readerRef.current = reader;
      let decoder = new TextDecoder("utf-8");

      let startRes = await startFunc?.();

      let fullMessage = "";
      let buffer = "";
      while(true){
        const {value, done} = await reader?.read?.() || {};
        if(done) break;
        let chunk = decoder.decode(value, {stream: true});
        fullMessage += chunk;
        if(lineStream){
          buffer += chunk;
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";
          await aliveStreamer?.(startRes, chunk, lines);
        }else{
          await aliveStreamer?.(startRes, chunk, fullMessage);
        }
      }

      await endFunc?.(startRes);

      return {
        Success: true
      };

    }catch(e){
      error?.(e);
      return {Success: false};
    }
        
  }

}

export default ReqX;